/**
 * Login loop guard (#107).
 *
 * If the session never sticks after a successful login (e.g. the browser
 * blocks the session cookie), the app would bounce app → login → app
 * forever. Each automatic redirect to the login is recorded per tab; after
 * LOGIN_LOOP_MAX redirects within LOGIN_LOOP_WINDOW_MS the app stops and
 * explains instead. A confirmed session clears the record.
 */

import { logger } from './logger';
import { STORAGE_KEYS } from './app-constants';

const STORAGE_KEY = STORAGE_KEYS.LOGIN_REDIRECTS;

/** Redirects within the window after which the next one is refused. */
const LOGIN_LOOP_MAX = 2;

export const LOGIN_LOOP_WINDOW_MS = 2 * 60 * 1000;

/** The accessor itself throws in some private-browsing configurations. */
function sessionStorageOrNull(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

type MinimalStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function readTimestamps(storage: MinimalStorage | null): number[] {
  try {
    const parsed: unknown = JSON.parse(storage?.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(parsed)
      ? parsed.filter((t): t is number => typeof t === 'number')
      : [];
  } catch {
    return [];
  }
}

function recent(timestamps: number[], now: number): number[] {
  return timestamps.filter((t) => now - t <= LOGIN_LOOP_WINDOW_MS);
}

/** Remember an automatic redirect to the login. */
export function recordLoginRedirect(
  now: number = Date.now(),
  storage: MinimalStorage | null = sessionStorageOrNull()
): void {
  try {
    const timestamps = [...recent(readTimestamps(storage), now), now];
    storage?.setItem(STORAGE_KEY, JSON.stringify(timestamps));
  } catch (error) {
    logger.warn('Could not record login redirect:', error);
  }
}

/** True when another automatic redirect would continue a loop. */
export function isLoginLooping(
  now: number = Date.now(),
  storage: MinimalStorage | null = sessionStorageOrNull()
): boolean {
  return recent(readTimestamps(storage), now).length >= LOGIN_LOOP_MAX;
}

/** Forget recorded redirects (session confirmed, or the user asked to retry). */
export function clearLoginRedirects(
  storage: MinimalStorage | null = sessionStorageOrNull()
): void {
  try {
    storage?.removeItem(STORAGE_KEY);
  } catch (error) {
    logger.warn('Could not clear login redirects:', error);
  }
}
